import React, { useState } from 'react';
import { Box, Button, Paper, TextField, Typography, Stack, Divider } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { toast } from 'react-toastify';
import { DataService } from 'src/services/data-service';

const ProfileDetail: React.FC = () => {
  const userState = useSelector((state: RootState) => state.user);
  const { token, user, active } = userState;

  const [showChangePassword, setShowChangePassword] = useState<boolean>(false);
  const [oldPassword, setOldPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error('All password fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirm password do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await DataService.patch('api/user/change-password', {
        CurrentPassword: oldPassword,
        NewPassword: newPassword,
        ConfirmPassword: confirmPassword
      });
      if (response.ok) {
        toast.success('Password updated successfully.');
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowChangePassword(false);
      } else {
        const result = await response.json();
        toast.error(result.message || 'Failed to update password.');
      }
    } catch (error: any) {
      toast.error(error.message || 'Error updating password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 600,
        margin: 'auto',
        mt: 4,
        borderRadius: 2,
      }}
    >
      <Typography variant="h5" sx={{ mb: 2 }}>
        Profile Details
      </Typography>

      {/* User Information */}
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography variant="body1">
          <strong>Email:</strong> {user.username}
        </Typography>
        <Typography variant="body1">
          <strong>Status:</strong> {active ? 'Active' : 'Inactive'}
        </Typography>
        <Typography variant="body1">
          <strong>Role:</strong> {user.role}
        </Typography>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Change Password Toggle */}
      <Button
        variant="contained"
        onClick={() => setShowChangePassword((prev) => !prev)}
        sx={{ mb: 2 }}
      >
        {showChangePassword ? 'Cancel Change Password' : 'Change Password'}
      </Button>

      {/* Change Password Form */}
      {showChangePassword && (
        <Box component="form" noValidate autoComplete="off">
          <Stack spacing={2}>
            <TextField
              label="Current Password"
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              disabled={submitting}
              fullWidth
            />
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={submitting}
              fullWidth
            />
            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={submitting}
              fullWidth
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleChangePassword}
              disabled={submitting}
            >
              Update Password
            </Button>
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

export default ProfileDetail;
